import AgoraRTC, {
  ConnectionState,
  IAgoraRTCClient,
  IAgoraRTCRemoteUser,
} from 'agora-rtc-sdk-ng';
import {useCallback, useEffect, useRef, useState} from 'react';

import {EnhancedConnectionState} from '../types/EnhancedConnectionState';

export interface ViewingSessionRequest {
  rtcToken: string;
  signalingToken: string;
  appId: string;
  viewingSessionUserId: number;
  expireTime: number;
}

interface SyncMessage {
  type: 'sync';
  position: number;
}

// Create a client factory to ensure we follow singleton pattern
const createRTCClient = () => {
  return AgoraRTC.createClient({
    mode: 'rtc',
    codec: 'vp8',
  });
};

export const useRTCClient = (
  tourId: string,
  viewingSessionRequest: ViewingSessionRequest,
  isHost = false
) => {
  // Enable Agora logging for debugging
  AgoraRTC.setLogLevel(process.env.NODE_ENV === 'development' ? 0 : 2);
  const [isInCall, setIsInCall] = useState(false);
  const [
    connectionState,
    setConnectionState,
  ] = useState<EnhancedConnectionState>('DISCONNECTED');
  const [remoteUsers, setRemoteUsers] = useState<IAgoraRTCRemoteUser[]>([]);
  const [connectionError, setConnectionError] = useState<Error | null>(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  const rtcClient = useRef<IAgoraRTCClient | null>(createRTCClient());
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleUserPublished = useCallback(
    async (user: IAgoraRTCRemoteUser, mediaType: 'audio' | 'video') => {
      if (!rtcClient.current) return;

      try {
        await rtcClient.current.subscribe(user, mediaType);

        setRemoteUsers((prevUsers) => {
          if (!prevUsers.some((u) => u.uid === user.uid)) {
            return [...prevUsers, user];
          }
          return prevUsers;
        });

        // Ensure we give the DOM a moment to update if needed
        setTimeout(() => {
          if (mediaType === 'video' && user.videoTrack) {
            try {
              const container = document.getElementById(
                `remote-video-${user.uid}`
              );
              if (container) {
                user.videoTrack.play(`remote-video-${user.uid}`, {
                  fit: 'cover',
                });
                console.log(`Playing video for user ${user.uid} in container`);
              } else {
                console.error(`Container remote-video-${user.uid} not found`);
              }
            } catch (err) {
              console.error(`Error playing video for user ${user.uid}:`, err);
            }
          }
          if (mediaType === 'audio' && user.audioTrack) {
            try {
              user.audioTrack.play();
              console.log(`Playing audio for user ${user.uid}`);
            } catch (err) {
              console.error(`Error playing audio for user ${user.uid}:`, err);
            }
          }
        }, 100);
      } catch (error) {
        console.error(
          `Failed to subscribe to ${mediaType} from user ${user.uid}:`,
          error
        );
      }
    },
    []
  );

  const handleUserUnpublished = useCallback((user: IAgoraRTCRemoteUser) => {
    setRemoteUsers((prevUsers) => prevUsers.filter((u) => u.uid !== user.uid));
  }, []);

  const handleUserLeft = useCallback((user: IAgoraRTCRemoteUser) => {
    setRemoteUsers((prevUsers) => prevUsers.filter((u) => u.uid !== user.uid));
  }, []);

  const handleConnectionStateChange = useCallback((state: ConnectionState) => {
    setConnectionState(state);

    if (state === 'DISCONNECTED') {
      setRemoteUsers([]);
    } else if (state === 'CONNECTED') {
      setConnectionError(null);
      setReconnectAttempts(0);
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    }
  }, []);

  const handleTokenExpired = useCallback(async () => {
    try {
      // Implement token renewal logic
      // This would typically involve a server call to get a new token
      // const newToken = await fetchNewToken(tourId);
      // await rtcClient.current?.renewToken(newToken);
      console.log('Token expired - renewal would happen here');
    } catch (error) {
      console.error('Failed to renew token:', error);
      setConnectionError(new Error('Session expired. Please rejoin.'));
    }
  }, []);

  const attemptReconnect = useCallback(() => {
    const MAX_RECONNECT_ATTEMPTS = 3;
    const RECONNECT_DELAY = 3000; // 3 seconds

    if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
      setConnectionState('RECONNECTING');
      setReconnectAttempts((prev) => prev + 1);

      reconnectTimerRef.current = setTimeout(async () => {
        try {
          if (rtcClient.current && tourId && viewingSessionRequest.rtcToken) {
            await rtcClient.current.join(
              viewingSessionRequest.appId,
              tourId,
              viewingSessionRequest.rtcToken,
              viewingSessionRequest.viewingSessionUserId
            );
            setConnectionState('CONNECTED');
          }
        } catch (error) {
          console.error('Reconnection attempt failed:', error);
          attemptReconnect();
        }
      }, RECONNECT_DELAY);
    } else {
      setConnectionState('ERROR');
      setConnectionError(
        new Error('Failed to reconnect after multiple attempts')
      );
    }
  }, [reconnectAttempts, tourId, viewingSessionRequest]);

  const handleException = useCallback(
    (event) => {
      console.error('RTC exception:', event);
      setConnectionError(new Error(`Connection error: ${event.code}`));

      if (
        [
          'NETWORK_TIMEOUT',
          'OPERATION_ABORTED',
          'ICE_TRANSPORT_ERROR',
        ].includes(event.code)
      ) {
        attemptReconnect();
      }
    },
    [attemptReconnect]
  );

  // Set up event listeners for RTC client
  useEffect(() => {
    if (!rtcClient.current) return;

    const client = rtcClient.current;

    client.on('connection-state-change', handleConnectionStateChange);
    client.on('user-published', handleUserPublished);
    client.on('user-unpublished', handleUserUnpublished);
    client.on('user-left', handleUserLeft);
    // client.on('token-privilege-will-expire', handleTokenExpired);
    // client.on('token-privilege-did-expire', handleTokenExpired);
    // client.on('exception', handleException);

    client.on('stream-message', (uid: number, data: Uint8Array) => {
      try {
        const message = JSON.parse(
          new TextDecoder().decode(data)
        ) as SyncMessage;
        if (message.type === 'sync') {
          console.log('Received scroll position:', message.position);
        }
      } catch (error) {
        console.error('Error parsing stream message:', error);
      }
    });

    return () => {
      client.removeAllListeners();
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
    };
  }, [
    handleConnectionStateChange,
    handleUserPublished,
    handleUserUnpublished,
    handleUserLeft,
  ]);

  return {
    rtcClient: rtcClient.current,
    isInCall,
    setIsInCall,
    connectionState,
    remoteUsers,
    connectionError,
    tourId,
    viewingSessionRequest,
    isHost,
    reconnectAttempts,
    resetConnection: () => {
      setConnectionError(null);
      setReconnectAttempts(0);
      setConnectionState('DISCONNECTED');
    },
  };
};
